import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import Button from "../../components/ui/button/Button";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../components/ui/table";
import Badge from "../../components/ui/badge/Badge";
import { Modal } from "../../components/ui/modal";
import type { ClienteB2C, ReservaB2C, Payment } from "../../hooks/useApi";
import { DollarLineIcon, TimeIcon, EnvelopeIcon } from "../../icons";
import CreateClientModal from "../../components/clients/CreateClientModal";
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import useAuth from "../../hooks/useAuth";
import RoleBasedRenderer from "../../components/auth/RoleBasedRenderer";
import { Eye, Edit, Trash2, FileText, Clock } from "lucide-react";

// Definimos el alias para mayor claridad (si se requiere en otros módulos)
export type ClienteB2CExtended = ClienteB2C;

// Añadir esta definición de tipo al inicio del archivo
type MetodoPago = "efectivo" | "tarjeta" | "transferencia";

// Actualizar la interfaz de datos financieros
interface FinancialData {
  metodoPago: MetodoPago;
  saldoPendiente: number;
  ultimoPago: string | null;
  historialPagos: ReservaB2C[];
  metodoPagos?: Payment[]; 
}

// Agregar esta interfaz para los tipos de correo
interface EmailTemplate {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

// Agregar los templates de correo disponibles
const emailTemplates: EmailTemplate[] = [
  {
    id: 'booking-confirmation',
    title: 'Confirmación de reservas',
    description: 'Envía un correo con los detalles de las reservas realizadas y la información de pago.',
    icon: (
      <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'payment-reminder',
    title: 'Recordatorio de pago pendiente',
    description: 'Notifica sobre pagos pendientes y proporciona instrucciones para completar la transacción.',
    icon: (
      <svg className="w-6 h-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'availability-alert',
    title: 'Alerta de disponibilidad',
    description: 'Informa sobre excursiones con plazas limitadas para promover reservas anticipadas.',
    icon: (
      <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
];

export interface ClientListProps {
  clients: ClienteB2C[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onAddClient: (client: Omit<ClienteB2C, "id" | "reservations">) => Promise<ClienteB2C>;
  onEditClient: (client: ClienteB2C) => Promise<void>;
  onDeleteClient: (clientId: number) => void;
  filterStatus: string;
  onFilterChange: (value: string) => void;
  getClientById: (id: number) => Promise<ClienteB2C>;
  getClientHistory: (id: number) => Promise<ReservaB2C[]>;
  getClientPayments?: (id: number) => Promise<Payment[]>; 
  openAddClientModal?: () => void;
}

const ClientList: React.FC<ClientListProps> = ({
  clients,
  searchTerm,
  onSearchChange,
  onAddClient,
  onEditClient,
  onDeleteClient,
  filterStatus,
  onFilterChange,
  getClientById,
  getClientHistory,
  getClientPayments, 
  openAddClientModal,
}) => {
  // Filter clients based on search term and status
  const filteredClients = clients.filter(client => {
    // Search filter
    const matchesSearch = !searchTerm || searchTerm.trim() === "" || 
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone.toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter - show all if no filter is selected
    const matchesStatus = !filterStatus || filterStatus === "" || filterStatus === "all" || 
      (filterStatus === "active" && client.active) ||
      (filterStatus === "inactive" && !client.active);

    return matchesSearch && matchesStatus;
  });

  // Estados de modales existentes
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyReservations, setHistoryReservations] = useState<ReservaB2C[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClienteB2C | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const [newClientData, setNewClientData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
    documentoIdentidad: "",
  });

  interface EditClientData {
    name: string;
    email: string;
    phone: string;
    active: boolean;
    documentoIdentidad: string;
  }

  const [editClientData, setEditClientData] = useState<EditClientData>({
    name: "",
    email: "",
    phone: "",
    active: true,
    documentoIdentidad: "",
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [clientIdToDelete, setClientIdToDelete] = useState<number | null>(null);

  // Agregar el estado para el modal financiero
  const [isFinancialModalOpen, setIsFinancialModalOpen] = useState(false);
  const [financialData, setFinancialData] = useState<FinancialData>({
    metodoPago: "efectivo",
    saldoPendiente: 0,
    ultimoPago: null,
    historialPagos: []
  });

  // Agregar el estado para el modal de correos
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  // Add useEffect to refetch payment data when financial modal opens
  useEffect(() => {
    if (isFinancialModalOpen && selectedClient && getClientPayments) {
      console.log("Financial modal opened, refetching payments for client:", selectedClient.id);
      
      const refetchPayments = async () => {
        try {
          const payments = await getClientPayments(selectedClient.id);
          console.log("Refetched payments:", payments);
          
          if (payments && payments.length > 0) {
            setFinancialData(prev => ({
              ...prev,
              metodoPagos: payments
            }));
            console.log("Updated financial data with refetched payments");
          } else {
            console.log("No payments found in refetch");
          }
        } catch (error) {
          console.error("Error refetching payments:", error);
        }
      };
      
      refetchPayments();
    }
  }, [isFinancialModalOpen, selectedClient, getClientPayments]);

  // Add useAuth hook
  const { canCreate, canEdit, canDelete } = useAuth();

  const handleAddClientSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (!newClientData.name || !newClientData.email || !newClientData.phone) {
        toast.error("Por favor complete todos los campos requeridos");
        return;
      }
      await onAddClient({ 
        name: newClientData.name,
        email: newClientData.email,
        phone: newClientData.phone,
        documentoIdentidad: newClientData.documentoIdentidad || undefined,
        active: true,
      });
      toast.success("Cliente creado exitosamente");
      setIsAddModalOpen(false);
      setNewClientData({
        name: "",
        email: "",
        phone: "",
        address: "",
        notes: "",
        documentoIdentidad: "",
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al crear el cliente");
    }
  };

  const handleEditClientSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (selectedClient) {
      try {
        await onEditClient({
          ...selectedClient,
          name: editClientData.name,
          email: editClientData.email,
          phone: editClientData.phone,
          documentoIdentidad: editClientData.documentoIdentidad?.trim() || undefined,
          active: editClientData.active,
          reservations: selectedClient.reservations
        });
        setIsEditModalOpen(false);
        toast.success("Cliente actualizado exitosamente");
      } catch (error) {
        toast.error("Error al actualizar el cliente");
      }
    }
  };

  // Función para abrir modal de Historial de Reservas
  const handleHistoryClick = async (client: ClienteB2C) => {
    try {
      const history = await getClientHistory(client.id);
      setHistoryReservations(history);
      setSelectedClient(client);
      setIsHistoryModalOpen(true);
    } catch (error) {
      toast.error("Error al obtener el historial del cliente");
    }
  };

  // Agregar esta función
  const handleFinancialClick = async (client: ClienteB2C) => {
    try {
      // Get the client's reservations for calculating pending balance
      const history = await getClientHistory(client.id);
      
      // Calculate pending balance from all reservations
      const saldoPendiente = history.reduce((total, reserva) => total + reserva.saldoPendiente, 0);
      
      // Default payment method
      const metodoPago = "efectivo" as MetodoPago;

      // Get payments from payment records (preferred method)
      let payments: Payment[] = [];
      if (getClientPayments) {
        try {
          payments = await getClientPayments(client.id);
          console.log('Client payments retrieved:', payments);
        } catch (error) {
          console.error("Error getting client payments:", error);
        }
      } else {
        console.log('getClientPayments function not provided');
      }

      // Find latest payment date
      const ultimoPago = payments.length > 0 
        ? payments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0].date
        : history.filter(res => res.montoFacturado > 0)
            .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())[0]?.fecha || null;

      setSelectedClient(client);
      setFinancialData({
        metodoPago,
        saldoPendiente,
        ultimoPago,
        historialPagos: history.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()),
        metodoPagos: payments
      });
      
      console.log('Financial data set:', {
        metodoPago,
        saldoPendiente,
        ultimoPago,
        historialPagosCount: history.length,
        metodoPagosCount: payments.length
      });
      
      setIsFinancialModalOpen(true);
    } catch (error) {
      console.error("Error in handleFinancialClick:", error);
      toast.error("Error al obtener la información financiera del cliente");
    }
  };

  // Add these helper objects at the top of the file with other constants
  const estadoDisplay = {
    pending_payment: "Pendiente",
    partial_payment: "Pago parcial",
    paid: "Pagada",
    no_show: "No Show",
    completed: "Completada"
  };

  function getStatusBadgeColor(status: string): "primary" | "success" | "error" | "warning" | "info" | "light" {
    switch (status) {
      case "pending_payment":
        return "primary";
      case "partial_payment":
        return "info";
      case "paid":
        return "success";
      case "no_show":
        return "warning";
      case "completed":
        return "success";
      default:
        return "light";
    }
  }

  const handleClientCreated = (newClient: ClienteB2C) => {
    toast.success("Cliente creado exitosamente");
    onSearchChange("");
  };

  const handleEditClick = (client: ClienteB2C) => {
    setSelectedClient(client);
    setEditClientData({
      name: client.name,
      email: client.email,
      phone: client.phone,
      active: client.active,
      documentoIdentidad: client.documentoIdentidad || "",
    });
    setIsEditModalOpen(true);
  };

  const handleDetailsClick = (client: ClienteB2C) => {
    setSelectedClient(client);
    setIsDetailsModalOpen(true);
  };

  const handleExportToExcel = () => {
    try {
      // Prepare the data for export
      const exportData = filteredClients.map(client => ({
        'Nombre': client.name,
        'Correo electrónico': client.email,
        'Teléfono': client.phone,
        'Documento de identidad': client.documentoIdentidad || 'No especificado',
        'Reservas': client.reservations,
        'Estado': client.active ? 'Activo' : 'Inactivo',
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Clientes');

      // Generate file name with current date
      const fileName = `clientes_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Save file
      XLSX.writeFile(wb, fileName);
      
      toast.success('Lista de clientes exportada exitosamente');
    } catch (error) {
      toast.error('Error al exportar la lista de clientes');
    }
  };

  // Add pagination state variables after existing state declarations
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Number of clients to show per page

  // Add this reset to first page effect after the component's existing useEffect blocks
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  // Pagination controls
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Add these calculations after the filteredClients declaration
  // Calculate pagination values
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const paginatedClients = filteredClients.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div>
      {/* Encabezado y filtros */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
            Gestión de clientes
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {filteredClients.length} {filteredClients.length === 1 ? 'cliente encontrado' : 'clientes encontrados'}
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <div className="flex flex-col md:flex-row gap-2">
            <input
              type="text"
              placeholder="Buscar clientes..."
              value={searchTerm}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
            />
            <select
              value={filterStatus}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => onFilterChange(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>
          <div className="flex gap-2">
            <RoleBasedRenderer requiredPermission="create">
              <Button variant="primary" onClick={() => setIsAddModalOpen(true)} className="w-full md:w-auto">
                Agregar cliente
              </Button>
            </RoleBasedRenderer>
            <Button 
              variant="outline" 
              className="w-full md:w-auto"
              onClick={handleExportToExcel}
            >
              Exportar a Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Tabla principal de clientes */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <TableHeader className="bg-gray-50 dark:bg-gray-800">
            <TableRow>
              <TableCell isHeader className="w-[40%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Nombre
              </TableCell>
              <TableCell isHeader className="w-[20%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Reservas
              </TableCell>
              <TableCell isHeader className="w-[20%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Estado
              </TableCell>
              <TableCell isHeader className="w-[20%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Acciones
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedClients.length > 0 ? (
              paginatedClients.map((client) => (
                <TableRow key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{client.name}</TableCell>
                  <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{client.reservations}</TableCell>
                  <TableCell className="px-4 py-4 text-sm whitespace-nowrap">
                    <Badge variant="light" color={client.active ? "success" : "error"}>
                      {client.active ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-4 py-4 text-sm whitespace-nowrap">
                    <div className="flex space-x-2 items-center">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="p-2" 
                        aria-label="Historial"
                        onClick={() => { setSelectedClient(client); handleHistoryClick(client); }}>
                        <Clock size={16} />
                      </Button>
                      <RoleBasedRenderer requiredPermission="edit">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="p-2" 
                          aria-label="Editar"
                          onClick={() => { handleEditClick(client); }}>
                          <Edit size={16} />
                        </Button>
                      </RoleBasedRenderer>
                      <RoleBasedRenderer requiredPermission="delete">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="p-2" 
                          aria-label="Eliminar"
                          onClick={() => {
                            setClientIdToDelete(client.id);
                            setIsDeleteModalOpen(true);
                          }}>
                          <Trash2 size={16} />
                        </Button>
                      </RoleBasedRenderer>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="p-2" 
                        aria-label="Financiero"
                        onClick={() => handleFinancialClick(client)}>
                        <DollarLineIcon className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="p-2" 
                        aria-label="Detalles"
                        onClick={() => handleDetailsClick(client)}>
                        <Eye size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} className="px-4 py-4 text-sm text-center text-gray-500 dark:text-gray-400">
                  {searchTerm ? `No se encontraron resultados para "${searchTerm}"` : "No hay clientes registrados"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Reemplazar el modal actual por CreateClientModal */}
      <CreateClientModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddClient={onAddClient}
        onSuccess={handleClientCreated}
      />

      {/* Modal Editar Cliente */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Editar cliente">
        <form onSubmit={handleEditClientSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nombre</label>
            <input
              type="text"
              value={editClientData.name}
              onChange={(e) => setEditClientData({ ...editClientData, name: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Correo electrónico</label>
            <input
              type="email"
              value={editClientData.email}
              onChange={(e) => setEditClientData({ ...editClientData, email: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Teléfono</label>
            <input
              type="text"
              value={editClientData.phone}
              onChange={(e) => setEditClientData({ ...editClientData, phone: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Documento de identidad</label>
            <input
              type="text"
              value={editClientData.documentoIdentidad}
              onChange={(e) => setEditClientData({ ...editClientData, documentoIdentidad: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              placeholder="Ingrese el número de documento"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={editClientData.active}
              onChange={(e) => setEditClientData({ ...editClientData, active: e.target.checked })}
              className="mr-2 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-800"
            />
            <span className="text-gray-700 dark:text-gray-300">Activo</span>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button 
              variant="outline" 
              type="button"
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button 
              variant="primary"
              type="submit"
            >
              Guardar cambios
            </Button>
          </div>
        </form>
      </Modal>

      <Modal 
        isOpen={isHistoryModalOpen} 
        onClose={() => setIsHistoryModalOpen(false)} 
        title={`Historial de reservas ${selectedClient ? `de ${selectedClient.name}` : ""}`}
        size="xl"
      >
        <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="overflow-x-auto">
            {historyReservations.length > 0 ? (
              <div className="min-w-full">
                <Table className="w-full">
                  <TableHeader className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                    <TableRow>
                      <TableCell isHeader className="w-[30%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Excursión
                      </TableCell>
                      <TableCell isHeader className="w-[12%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Fecha
                      </TableCell>
                      <TableCell isHeader className="w-[18%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Personas
                      </TableCell>
                      <TableCell isHeader className="w-[12%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Estado
                      </TableCell>
                      <TableCell isHeader className="w-[10%] px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Total
                      </TableCell>
                      <TableCell isHeader className="w-[10%] px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Facturado
                      </TableCell>
                      <TableCell isHeader className="w-[8%] px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Pendiente
                      </TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {historyReservations.map((res) => (
                      <TableRow key={res.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <TableCell className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 truncate">
                          {res.excursion}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {new Date(res.fecha).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {res.adultos} adultos{res.ninios > 0 && `, ${res.ninios} niños`}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm whitespace-nowrap">
                          <Badge color={getStatusBadgeColor(res.estado)}>
                            {estadoDisplay[res.estado]}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          ${res.precioTotal.toFixed(2)}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          ${res.montoFacturado.toFixed(2)}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          ${res.saldoPendiente.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-center py-4 text-gray-500 dark:text-gray-400">
                No se encontraron reservas.
              </p>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setClientIdToDelete(null);
        }}
        title="Confirmación de eliminación"
        size="md"
      >
        <div className="space-y-6 px-2">
          <div className="space-y-4">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
              ¿Está seguro de eliminar este cliente?
            </p>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción eliminará permanentemente todos los datos asociados al cliente, incluyendo:
              </p>
              
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 ml-4 space-y-1">
                <li>Información personal del cliente</li>
                <li>Historial completo de reservas</li>
                <li>Registros de pagos y transacciones</li>
                <li>Cualquier otro dato relacionado</li>
              </ul>
            </div>

            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setClientIdToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                if (clientIdToDelete !== null) {
                  try {
                    await onDeleteClient(clientIdToDelete);
                    toast.success("Cliente eliminado exitosamente");
                    setIsDeleteModalOpen(false);
                    setClientIdToDelete(null);
                  } catch (error) {
                    toast.error("Error al eliminar el cliente");
                  }
                }
              }}
            >
              Sí, eliminar cliente
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isFinancialModalOpen}
        onClose={() => setIsFinancialModalOpen(false)}
        title="Información financiera del cliente"
        size="lg"
      >
        <div className="space-y-4 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Pagos Pendientes */}
            <div className="flex flex-col p-3 bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <DollarLineIcon className="w-5 h-5 text-red-500 dark:text-red-400" />
                </div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Saldo pendiente
                </h3>
              </div>
              <p className={`text-xl font-semibold ${
                financialData.saldoPendiente > 0 
                  ? 'text-red-600 dark:text-red-400' 
                  : 'text-green-600 dark:text-green-400'
              }`}>
                ${financialData.saldoPendiente.toFixed(2)}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                {financialData.saldoPendiente > 0 ? 'Tiene pagos pendientes' : 'Al día con los pagos'}
              </p>
            </div>

            {/* Fecha de Último Pago */}
            <div className="flex flex-col p-3 bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <TimeIcon className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                </div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Último pago
                </h3>
              </div>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {financialData.ultimoPago ? (
                  new Date(financialData.ultimoPago).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })
                ) : (
                  "Sin pagos"
                )}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                Fecha del último pago registrado
              </p>
            </div>
          </div>

          {/* Historial de Pagos Recientes */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Historial de pagos
              </h4>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {financialData.metodoPagos ? financialData.metodoPagos.length : 
                  financialData.historialPagos.reduce((total, reserva) => 
                  total + ((reserva.montoFacturado > 0) ? 1 : 0), 0)} pagos
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
                <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <TableHeader className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                    <TableRow>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Fecha
                      </TableCell>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Excursión
                      </TableCell>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Monto pagado
                      </TableCell>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Método
                      </TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {financialData.metodoPagos && financialData.metodoPagos.length > 0 ? (
                      // Display all payments from payment records
                      financialData.metodoPagos
                        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                        .map(payment => (
                        <TableRow key={`payment-${payment.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <TableCell className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {new Date(payment.date).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </TableCell>
                          <TableCell className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {payment.reservation?.excursion || 
                             (payment.reservationId ? `Reserva #${payment.reservationId}` : 'Pago directo')}
                          </TableCell>
                          <TableCell className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            ${payment.amount.toFixed(2)}
                          </TableCell>
                          <TableCell className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 capitalize">
                            {payment.method || 'efectivo'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      // Fallback if no payment records available
                      <TableRow>
                        <TableCell colSpan={4} className="px-4 py-4 text-sm text-center text-gray-500 dark:text-gray-400">
                          No se encontraron pagos registrados
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        title={selectedClient?.name || "Detalles del cliente"}
        size="md"
      >
        <div className="space-y-4">
          {/* Información Personal y Estado */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Información Personal */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                Información personal
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {selectedClient?.email}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Teléfono</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {selectedClient?.phone}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Documento de identidad</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {selectedClient?.documentoIdentidad || "No especificado"}
                  </p>
                </div>
              </div>
            </div>

            {/* Estado y Reservas */}
            <div className="space-y-4">
              {/* Estado */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Estado
                </h3>
                <div className="flex items-center justify-between">
                  <Badge color={selectedClient?.active ? "success" : "error"}>
                    {selectedClient?.active ? "Activo" : "Inactivo"}
                  </Badge>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Estado actual
                  </p>
                </div>
              </div>

              {/* Reservas */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Total de reservas
                </h3>
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {selectedClient?.reservations || 0}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Reservas realizadas
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button variant="primary" onClick={() => setIsDetailsModalOpen(false)}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        title="Enviar correo electrónico"
        size="lg"
      >
        <div className="space-y-6">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Selecciona el tipo de correo que deseas enviar a <span className="font-medium text-gray-900 dark:text-gray-100">{selectedClient?.name}</span>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {emailTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => {
                  // Aquí iría la lógica para enviar el correo
                  toast.success(`Correo de ${template.title.toLowerCase()} enviado correctamente`);
                  setIsEmailModalOpen(false);
                }}
                className="relative flex flex-col items-center p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 transition-all hover:border-indigo-500 dark:hover:border-indigo-500 hover:shadow-md group"
              >
                <div className="mb-4 p-3 rounded-full bg-gray-50 dark:bg-gray-700 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/30 transition-colors">
                  {template.icon}
                </div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 text-center mb-2">
                  {template.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  {template.description}
                </p>
                <div className="absolute inset-0 border-2 border-transparent rounded-xl group-hover:border-indigo-500 pointer-events-none transition-colors" />
              </button>
            ))}
          </div>

          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Información importante
                </h3>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  <p>
                    Los correos se enviarán a la dirección registrada: <span className="font-medium text-gray-900 dark:text-gray-100">{selectedClient?.email}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Add pagination controls after the table component */}
      {filteredClients.length > itemsPerPage && (
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 border-t border-gray-200 dark:border-gray-700 mt-4">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md 
                ${currentPage === 1 
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                border border-gray-300 dark:border-gray-600`}
            >
              Anterior
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
                ${currentPage === totalPages 
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                border border-gray-300 dark:border-gray-600`}
            >
              Siguiente
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Mostrando <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredClients.length)}</span> a{" "}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredClients.length)}</span> de{" "}
                <span className="font-medium">{filteredClients.length}</span> clientes
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                    ${currentPage === 1 
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <span className="sr-only">Anterior</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers - limited to 5 pages */}
                {(() => {
                  // Calculate which page numbers to show (max 5)
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  
                  // Adjust startPage if we're near the end
                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                          ${currentPage === i
                            ? 'z-10 bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                    ${currentPage === totalPages 
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <span className="sr-only">Siguiente</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientList;